// genesys-telephony-facade.service.ts
import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { GenesysIconsStates } from '../enums/Icons';
import { GenesysUserStates } from '../enums/States';
import { GenesysBridgeService } from './genesys-bridge.service';

@Injectable({
  providedIn: 'root',
})
export class GenesysFacadeService {
  public readonly stateInteractionEvent$ =
    this.genesysBridgeService.interactionSubscription$.pipe(
      map((result) => result.data.interaction),
      map((interaction) =>
        interaction.old ? interaction.old.state : interaction.state
      )
    );

  public readonly categoryInteractionEvent$ =
    this.genesysBridgeService.interactionSubscription$.pipe(
      map((result) => result.data.category)
    );

  private readonly categoryChangeInteractionEvent$ =
    this.categoryInteractionEvent$.pipe(
      map((category) => category === 'change')
    );

  private readonly categoryDisconnectInteractionEvent$ =
    this.categoryInteractionEvent$.pipe(
      map((category) => category === 'disconnect')
    );

  public readonly callDirection$ = combineLatest([
    this.categoryChangeInteractionEvent$,
    this.categoryDisconnectInteractionEvent$,
    this.stateInteractionEvent$,
  ]).pipe(
    map(([change, disconnect, state]) => {
      return this.setCallDirection({ change, state, disconnect });
    }),
    startWith(null)
  );

  private readonly userState$ = this.genesysBridgeService.userStatus$.pipe(
    map((result) => result.data?.data?.status),
    startWith(GenesysUserStates.NOT_AVAILABLE)
  );

  public readonly icon$: Observable<string> = combineLatest([
    this.userState$,
    this.callDirection$,
  ]).pipe(
    map(([userState, callState]) => callState ?? userState),
    map((state) => {
      return (
        this.switchGenesysTelephonyIcon[state] ||
        this.switchGenesysTelephonyIcon['default']
      )(state);
    })
  );

  constructor(private genesysBridgeService: GenesysBridgeService) {}

  private switchGenesysTelephonyIcon = {
    default: (state: string) => {
      return GenesysIconsStates[state] || GenesysIconsStates.DEFAULT;
    },
    Outgoing: (state: string) => {
      return GenesysIconsStates.DIALLING;
    },
    Incoming: (state: string) => {
      return GenesysIconsStates.INCOMING;
    },
  };

  private setCallDirection(data: any): string | null {
    const { change, state, disconnect } = data;
    let callDirection = state === 'DIALING' && 'Outgoing';
    if (disconnect) {
      callDirection = null;
    } else if (change) {
      callDirection = this.switchCallDirection[state];
    }
    return callDirection;
  }

  private switchCallDirection = {
    ALERTING: 'Incoming',
    CONTACTING: 'Outgoing',
    DIALING: 'Outgoing',
  };
}
